import { FC, useEffect, useMemo } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
	resetPatchEasInfoSuccess,
	resetUpdateEasInfoSuccess,
	patchEasInfoRequest,
	updateEasInfoRequest,
} from 'redux/reducers/easInfo/reducer';
import {
	getEasInfoPatchLoading,
	getEasInfoPatchSuccess,
	getEasInfoUpdateLoading,
	getEasInfoUpdateSuccess,
} from 'redux/reducers/easInfo/selectors';
import { TEditSettingDialogProps } from './types';
import {
	BOOLEAN_VALUE_OPTIONS,
	EAS_INFO_BOOLEAN_TYPE,
	EAS_INFO_NUMBER_TYPE,
	isLongTextValue,
} from './constants';

const isBooleanType = (type: string): boolean =>
	type.trim().toUpperCase() === EAS_INFO_BOOLEAN_TYPE;

const isNumberType = (type: string): boolean => type.trim().toUpperCase() === EAS_INFO_NUMBER_TYPE;

const isValidNumberValue = (value: string): boolean => {
	const trimmedValue = value.trim();
	if (trimmedValue === '') return false;

	return !Number.isNaN(Number(trimmedValue));
};

const normalizeBooleanValue = (value: string): string =>
	BOOLEAN_VALUE_OPTIONS.some((option) => option.value === value) ? value : '';

export const EditSettingDialog: FC<TEditSettingDialogProps> = ({
	open,
	onClose,
	settingToEdit,
	groups,
}) => {
	const dispatch = useDispatch();
	const isCreateMode = !settingToEdit;

	const patchLoading = useSelector(getEasInfoPatchLoading);
	const patchSuccess = useSelector(getEasInfoPatchSuccess);
	const updateLoading = useSelector(getEasInfoUpdateLoading);
	const updateSuccess = useSelector(getEasInfoUpdateSuccess);

	const saveLoading = isCreateMode ? updateLoading : patchLoading;
	const saveSuccess = isCreateMode ? updateSuccess : patchSuccess;

	const groupOptions = useMemo(() => {
		const options = [...(groups ?? [])];
		if (
			settingToEdit?.groupHandle &&
			!options.some((group) => group.groupHandle === settingToEdit.groupHandle)
		) {
			options.push({
				groupHandle: settingToEdit.groupHandle,
				groupTitle: settingToEdit.groupTitle || settingToEdit.groupHandle,
			});
		}
		return options;
	}, [groups, settingToEdit]);

	const validationSchema = useMemo(
		() =>
			Yup.object({
				name: Yup.string().required('Name is required'),
				type: Yup.string().required('Type is required'),
				value: Yup.string()
					.required('Value is required')
					.test('value-by-type', function validateValueByType(value) {
						const { type } = this.parent as { type: string };

						if (!isNumberType(type ?? '')) {
							return true;
						}

						if (value == null || value.trim() === '') {
							return this.createError({ message: 'Value is required' });
						}

						if (!isValidNumberValue(value)) {
							return this.createError({ message: 'Value must be a valid number' });
						}

						return true;
					}),
			}),
		[],
	);

	const formik = useFormik({
		initialValues: {
			name: settingToEdit?.name ?? '',
			type: settingToEdit?.type ?? '',
			value: settingToEdit?.value ?? '',
			title: settingToEdit?.title ?? '',
			groupHandle: settingToEdit?.groupHandle ?? '',
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			const groupTitle =
				groupOptions.find((group) => group.groupHandle === values.groupHandle)?.groupTitle ?? '';

			if (isCreateMode) {
				dispatch(
					updateEasInfoRequest({
						name: values.name,
						type: values.type,
						value: values.value,
						title: values.title,
						groupHandle: values.groupHandle,
						groupTitle,
					}),
				);
				return;
			}

			dispatch(
				patchEasInfoRequest([
					{
						name: values.name,
						value: values.value,
						title: values.title,
						groupHandle: values.groupHandle,
						groupTitle,
					},
				]),
			);
		},
	});

	useEffect(() => {
		if (!saveSuccess) return;

		if (isCreateMode) {
			dispatch(resetUpdateEasInfoSuccess());
		} else {
			dispatch(resetPatchEasInfoSuccess());
		}

		formik.resetForm();
		onClose();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [saveSuccess, isCreateMode]);

	const handleClose = () => {
		formik.resetForm();
		onClose();
	};

	const isBooleanValueField = isBooleanType(formik.values.type);
	const isNumberValueField = isNumberType(formik.values.type);
	const isLongTextValueField =
		!isBooleanValueField && !isNumberValueField && isLongTextValue(formik.values.value);

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>{isCreateMode ? 'Add New Parameter' : 'Edit EAS Project Setting'}</DialogTitle>
			<DialogContent>
				<Box
					component="form"
					onSubmit={formik.handleSubmit}
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
				>
					<TextField
						label="Name"
						name="name"
						value={formik.values.name}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						size="small"
						disabled={!isCreateMode}
						fullWidth
						error={formik.touched.name && Boolean(formik.errors.name)}
						helperText={formik.touched.name && formik.errors.name}
					/>
					<TextField
						label="Type"
						name="type"
						value={formik.values.type}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						size="small"
						disabled={!isCreateMode}
						fullWidth
						error={formik.touched.type && Boolean(formik.errors.type)}
						helperText={formik.touched.type && formik.errors.type}
					/>
					<TextField
						label="Title"
						name="title"
						value={formik.values.title}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						size="small"
						fullWidth
					/>
					<FormControl fullWidth size="small">
						<InputLabel id="eas-info-group-label">Group</InputLabel>
						<Select
							labelId="eas-info-group-label"
							label="Group"
							name="groupHandle"
							value={formik.values.groupHandle}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
						>
							<MenuItem value="">
								<em>None</em>
							</MenuItem>
							{groupOptions.map((group) => (
								<MenuItem key={group.groupHandle} value={group.groupHandle}>
									{group.groupTitle}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					{isBooleanValueField ? (
						<FormControl
							fullWidth
							size="small"
							error={formik.touched.value && Boolean(formik.errors.value)}
						>
							<InputLabel id="eas-info-value-label">Value</InputLabel>
							<Select
								labelId="eas-info-value-label"
								label="Value"
								name="value"
								value={normalizeBooleanValue(formik.values.value)}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
							>
								{BOOLEAN_VALUE_OPTIONS.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</Select>
							{formik.touched.value && formik.errors.value && (
								<FormHelperText>{formik.errors.value}</FormHelperText>
							)}
						</FormControl>
					) : (
						<TextField
							label="Value"
							name="value"
							value={formik.values.value}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							size="small"
							type={isNumberValueField ? 'number' : 'text'}
							multiline={isLongTextValueField}
							minRows={isLongTextValueField ? 6 : undefined}
							fullWidth
							error={formik.touched.value && Boolean(formik.errors.value)}
							helperText={formik.touched.value && formik.errors.value}
						/>
					)}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={saveLoading}>
					Cancel
				</Button>
				<Button
					onClick={() => formik.handleSubmit()}
					variant="contained"
					disabled={saveLoading}
					startIcon={saveLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
				>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};
